import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  CircularProgress,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  IconButton,
  Tooltip,
  TextField,
  Card,
  CardContent,
  Tabs,
  Tab,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
} from "@mui/material";
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Visibility as ViewIcon,
  Pending as PendingIcon,
  CheckCircle as VerifiedIcon,
  DoneAll as DoneAllIcon,
  Block as BlockIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  CardMembership as PlanIcon,
} from "@mui/icons-material";
import { api_urls } from "../../../utils/api-urls";

const MilanSathiVerifications = () => {
  const [verifications, setVerifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalVerifications, setTotalVerifications] = useState(0);
  const [selectedVerification, setSelectedVerification] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("pending");
  
  // Search filters
  const [searchQuery, setSearchQuery] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Plan assignment
  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [planUser, setPlanUser] = useState(null);
  const [planLoading, setPlanLoading] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    fetchVerifications();
    fetchPlans();
  }, [page, rowsPerPage, statusFilter, searchQuery, startDate, endDate]);

  // Debounce search
  const [searchTimeout, setSearchTimeout] = useState(null);

  // Helper to check if value is a file path/URL or just text
  const isFilePath = (value) => {
    if (!value) return false;
    const fileExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.mp4', '.mov', '.avi', '.pdf'];
    const lowerValue = value.toLowerCase();
    return fileExtensions.some(ext => lowerValue.includes(ext)) || 
           value.startsWith('http') || 
           value.startsWith('/uploads') ||
           value.startsWith('uploads');
  };

  // Get full URL for file paths
  const getFullUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    if (path.startsWith('/')) return `https://api.bharatdarshan.app${path}`;
    return `https://api.bharatdarshan.app/${path}`;
  };

  const handleSearchChange = (value) => {
    if (searchTimeout) clearTimeout(searchTimeout);
    const timeout = setTimeout(() => {
      setSearchQuery(value);
      setPage(0);
    }, 500);
    setSearchTimeout(timeout);
  };

  const clearFilters = () => {
    setSearchQuery("");
    setStartDate("");
    setEndDate("");
    setPage(0);
  };

  const fetchPlans = async () => {
    try {
      const response = await fetch(`${api_urls}api/admins/plans`);
      const data = await response.json();
      if (data.success) {
        setPlans(data.data || []);
      }
    } catch (error) {
      console.error("Error fetching plans:", error);
    }
  };

  const fetchVerifications = async () => {
    setLoading(true);
    try {
      const statusParam = statusFilter !== "all" ? `&status=${statusFilter}` : "";
      const searchParam = searchQuery ? `&search=${encodeURIComponent(searchQuery)}` : "";
      const startDateParam = startDate ? `&startDate=${startDate}` : "";
      const endDateParam = endDate ? `&endDate=${endDate}` : "";
      const response = await fetch(
        `${api_urls}api/admins/verification/pending?page=${page + 1}&limit=${rowsPerPage}${statusParam}${searchParam}${startDateParam}${endDateParam}`
      );
      const data = await response.json();
      if (data.success) {
        setVerifications(data.data || []);
        setTotalVerifications(data.pagination?.total || 0);
      } else {
        setVerifications([]);
        setTotalVerifications(0);
      }
    } catch (error) {
      console.error("Error fetching verifications:", error);
      setVerifications([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (verification) => {
    if (!window.confirm("Are you sure you want to approve this verification?")) return;
    
    setActionLoading(true);
    try {
      const userId = verification.user?._id || verification.userId;
      const response = await fetch(`${api_urls}api/admins/verifications/${userId}/approve`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
      });
      const data = await response.json();
      if (data.success) {
        setSnackbar({ open: true, message: "Verification approved successfully! User notified via FCM.", severity: "success" });
        fetchVerifications();
      } else {
        setSnackbar({ open: true, message: data.message || "Failed to approve verification", severity: "error" });
      }
    } catch (error) {
      console.error("Error approving verification:", error);
      setSnackbar({ open: true, message: "Error approving verification", severity: "error" });
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      alert("Please provide a reason for rejection");
      return;
    }
    
    setActionLoading(true);
    try {
      const userId = selectedVerification.user?._id || selectedVerification.userId;
      const response = await fetch(`${api_urls}api/admins/verifications/${userId}/reject`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason: rejectReason }),
      });
      const data = await response.json();
      if (data.success) {
        setSnackbar({ open: true, message: "Verification rejected successfully! User notified via FCM.", severity: "success" });
        setRejectDialogOpen(false);
        setRejectReason("");
        fetchVerifications();
      } else {
        setSnackbar({ open: true, message: data.message || "Failed to reject verification", severity: "error" });
      }
    } catch (error) {
      console.error("Error rejecting verification:", error);
      setSnackbar({ open: true, message: "Error rejecting verification", severity: "error" });
    } finally {
      setActionLoading(false);
    }
  };

  const handleAssignPlan = async () => {
    if (!selectedPlanId || !planUser) {
      setSnackbar({ open: true, message: "Please select a plan", severity: "warning" });
      return;
    }

    setPlanLoading(true);
    try {
      const userId = planUser.user?._id || planUser.userId;
      const response = await fetch(`${api_urls}api/admins/assign-plan`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, planId: selectedPlanId }),
      });
      const data = await response.json();
      if (data.success) {
        setSnackbar({ open: true, message: `Plan assigned successfully! User notified via FCM.`, severity: "success" });
        setPlanDialogOpen(false);
        setSelectedPlanId("");
        setPlanUser(null);
        fetchVerifications();
      } else {
        setSnackbar({ open: true, message: data.message || "Failed to assign plan", severity: "error" });
      }
    } catch (error) {
      console.error("Error assigning plan:", error);
      setSnackbar({ open: true, message: "Error assigning plan", severity: "error" });
    } finally {
      setPlanLoading(false);
    }
  };

  const openPlanDialog = (verification) => {
    setPlanUser(verification);
    setSelectedPlanId("");
    setPlanDialogOpen(true);
  };

  const handleViewDocuments = (verification) => {
    setSelectedVerification(verification);
    setViewDialogOpen(true);
  };

  const openRejectDialog = (verification) => {
    setSelectedVerification(verification);
    setRejectDialogOpen(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "approved": return "success";
      case "rejected": return "error";
      case "pending": return "warning";
      default: return "default";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "approved": return <VerifiedIcon sx={{ fontSize: 14 }} />;
      case "rejected": return <BlockIcon sx={{ fontSize: 14 }} />;
      case "pending": return <PendingIcon sx={{ fontSize: 14 }} />;
      default: return null;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "approved": return "Verified";
      case "rejected": return "Rejected";
      case "pending": return "Pending";
      case "not_submitted": return "Not Submitted";
      default: return status || "Unknown";
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: "bold" }}>
        Verification Management
      </Typography>

      {/* Status Filter Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={statusFilter}
          onChange={(e, newValue) => {
            setStatusFilter(newValue);
            setPage(0);
          }}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="Pending" value="pending" icon={<PendingIcon />} iconPosition="start" />
          <Tab label="Approved" value="approved" icon={<DoneAllIcon />} iconPosition="start" />
          <Tab label="Rejected" value="rejected" icon={<BlockIcon />} iconPosition="start" />
          <Tab label="All" value="all" />
        </Tabs>
      </Paper>

      {/* Search and Filter Section */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by Name, Email, or Phone..."
              defaultValue={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Start Date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setPage(0); }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="End Date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setPage(0); }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            {(searchQuery || startDate || endDate) && (
              <Button fullWidth variant="outlined" color="secondary" startIcon={<ClearIcon />} onClick={clearFilters}>
                Clear Filters
              </Button>
            )}
          </Grid>
        </Grid>
        {(searchQuery || startDate || endDate) && (
          <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
            <Typography variant="body2" color="textSecondary" sx={{ mr: 1, alignSelf: "center" }}>Active Filters:</Typography>
            {searchQuery && <Chip size="small" label={`Search: "${searchQuery}"`} onDelete={() => setSearchQuery("")} />}
            {startDate && <Chip size="small" label={`From: ${startDate}`} onDelete={() => setStartDate("")} />}
            {endDate && <Chip size="small" label={`To: ${endDate}`} onDelete={() => setEndDate("")} />}
          </Box>
        )}
      </Paper>

      {/* Stats Card */}
      <Paper sx={{ p: 2, mb: 3, backgroundColor: statusFilter === "pending" ? "#fff3e0" : statusFilter === "approved" ? "#e8f5e9" : statusFilter === "rejected" ? "#ffebee" : "#e3f2fd" }}>
        <Box display="flex" alignItems="center">
          {statusFilter === "pending" ? <PendingIcon sx={{ fontSize: 40, color: "#ed6c02", mr: 2 }} /> :
           statusFilter === "approved" ? <VerifiedIcon sx={{ fontSize: 40, color: "#4caf50", mr: 2 }} /> :
           statusFilter === "rejected" ? <BlockIcon sx={{ fontSize: 40, color: "#f44336", mr: 2 }} /> :
           <DoneAllIcon sx={{ fontSize: 40, color: "#1976d2", mr: 2 }} />}
          <Box>
            <Typography variant="h5" fontWeight="bold" color={statusFilter === "pending" ? "#ed6c02" : statusFilter === "approved" ? "#4caf50" : statusFilter === "rejected" ? "#f44336" : "#1976d2"}>
              {totalVerifications}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {statusFilter === "pending" ? "Pending verification requests" :
               statusFilter === "approved" ? "Approved verifications" :
               statusFilter === "rejected" ? "Rejected verifications" : "Total verifications"}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Verifications Table */}
      <TableContainer component={Paper}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
        ) : verifications.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={4}>
            <Box textAlign="center">
              <VerifiedIcon sx={{ fontSize: 60, color: "#4caf50", mb: 2 }} />
              <Typography variant="h6">No {statusFilter === "all" ? "" : statusFilter} Verifications</Typography>
              <Typography variant="body2" color="textSecondary">
                {statusFilter === "pending" ? "All verification requests have been processed" : "No records found"}
              </Typography>
            </Box>
          </Box>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell>User</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Plan</TableCell>
                  <TableCell>Documents</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Submitted</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {verifications.map((verification) => (
                  <TableRow key={verification._id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Avatar
                          src={getFullUrl(verification.user?.profileImageUrl)}
                          sx={{ mr: 2, width: 40, height: 40 }}
                        >
                          {verification.user?.firstName?.charAt(0) || verification.user?.name?.charAt(0) || "U"}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {verification.user?.name || `${verification.user?.firstName || ''} ${verification.user?.lastName || ''}`.trim() || "Unknown User"}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{verification.user?.email || "N/A"}</TableCell>
                    <TableCell>{verification.user?.phone || verification.user?.mobile || "N/A"}</TableCell>
                    <TableCell>
                      {verification.planName || verification.user?.planName ? (
                        <Chip label={verification.planName || verification.user?.planName} size="small" color="primary" variant="outlined" />
                      ) : (
                        <Chip label="No Plan" size="small" color="default" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1} flexWrap="wrap">
                        {verification.panCard && (
                          <Chip label={isFilePath(verification.panCard) ? "PAN Image" : "PAN: " + verification.panCard} size="small" color="primary" />
                        )}
                        {verification.aadhaarCard && (
                          <Chip label={isFilePath(verification.aadhaarCard) ? "Aadhaar Image" : "Aadhaar: " + verification.aadhaarCard.substring(0, 4) + "****"} size="small" color="secondary" />
                        )}
                        {verification.verificationVideo && (
                          <Chip label="Video" size="small" color="info" />
                        )}
                        {!verification.panCard && !verification.aadhaarCard && !verification.verificationVideo && (
                          <Typography variant="caption" color="textSecondary">No docs</Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={getStatusLabel(verification.status)} 
                        size="small" 
                        color={getStatusColor(verification.status || "pending")}
                        icon={getStatusIcon(verification.status || "pending")}
                      />
                      {verification.status === "rejected" && verification.adminNotes && (
                        <Typography variant="caption" display="block" color="error" sx={{ mt: 0.5, maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {verification.adminNotes}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{formatDate(verification.createdAt)}</TableCell>
                    <TableCell>
                      <Box display="flex" gap={0.5} flexWrap="wrap">
                        <Tooltip title="View Documents">
                          <IconButton size="small" color="primary" onClick={() => handleViewDocuments(verification)}>
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={verification.status === "approved" ? "Already Approved" : "Approve"}>
                          <span>
                            <IconButton size="small" color="success" onClick={() => handleApprove(verification)} disabled={actionLoading || verification.status === "approved"}>
                              <ApproveIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={verification.status === "rejected" ? "Already Rejected" : "Reject"}>
                          <span>
                            <IconButton size="small" color="error" onClick={() => openRejectDialog(verification)} disabled={actionLoading || verification.status === "rejected"}>
                              <RejectIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Assign Plan">
                          <IconButton size="small" color="secondary" onClick={() => openPlanDialog(verification)}>
                            <PlanIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={totalVerifications}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 20, 50]}
            />
          </>
        )}
      </TableContainer>

      {/* View Documents Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Verification Documents</DialogTitle>
        <DialogContent>
          {selectedVerification && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>User Details</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="textSecondary">Name</Typography>
                        <Typography>{selectedVerification.user?.name || `${selectedVerification.user?.firstName || ''} ${selectedVerification.user?.lastName || ''}`.trim() || "N/A"}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="textSecondary">Email</Typography>
                        <Typography>{selectedVerification.user?.email || "N/A"}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="textSecondary">Phone</Typography>
                        <Typography>{selectedVerification.user?.phone || selectedVerification.user?.mobile || "N/A"}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="textSecondary">Plan</Typography>
                        <Typography>{selectedVerification.planName || selectedVerification.user?.planName || (selectedVerification.user?.isPremium ? "Premium" : "Free")}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="textSecondary">Status</Typography>
                        <Chip label={getStatusLabel(selectedVerification.status)} size="small" color={getStatusColor(selectedVerification.status)} />
                      </Grid>
                      {selectedVerification.membershipExpiry && (
                        <Grid item xs={6}>
                          <Typography variant="body2" color="textSecondary">Plan Expiry</Typography>
                          <Typography>{formatDate(selectedVerification.membershipExpiry)}</Typography>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
              
              {selectedVerification.panCard && (
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom fontWeight="bold">PAN Card</Typography>
                      {isFilePath(selectedVerification.panCard) ? (
                        <img src={getFullUrl(selectedVerification.panCard)} alt="PAN Card" style={{ width: "100%", maxHeight: 300, objectFit: "contain", borderRadius: 8 }} />
                      ) : (
                        <Typography variant="h6" sx={{ p: 2, backgroundColor: "#f5f5f5", borderRadius: 2, textAlign: "center" }}>
                          {selectedVerification.panCard}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              )}
              
              {selectedVerification.aadhaarCard && (
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom fontWeight="bold">Aadhaar Card</Typography>
                      {isFilePath(selectedVerification.aadhaarCard) ? (
                        <img src={getFullUrl(selectedVerification.aadhaarCard)} alt="Aadhaar Card" style={{ width: "100%", maxHeight: 300, objectFit: "contain", borderRadius: 8 }} />
                      ) : (
                        <Typography variant="h6" sx={{ p: 2, backgroundColor: "#f5f5f5", borderRadius: 2, textAlign: "center" }}>
                          {selectedVerification.aadhaarCard.substring(0, 4)}{"****"}{selectedVerification.aadhaarCard.substring(selectedVerification.aadhaarCard.length - 4)}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              )}
              
              {selectedVerification.verificationVideo && (
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom fontWeight="bold">Verification Video</Typography>
                      <video src={getFullUrl(selectedVerification.verificationVideo)} controls style={{ width: "100%", maxHeight: 400, borderRadius: 8 }} />
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {selectedVerification.adminNotes && (
                <Grid item xs={12}>
                  <Alert severity="error" sx={{ mt: 1 }}>
                    <Typography variant="subtitle2">Rejection Reason:</Typography>
                    <Typography variant="body2">{selectedVerification.adminNotes}</Typography>
                  </Alert>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          <Button variant="outlined" color="secondary" startIcon={<PlanIcon />} onClick={() => { setViewDialogOpen(false); openPlanDialog(selectedVerification); }}>
            Assign Plan
          </Button>
          <Button variant="contained" color="success" startIcon={<ApproveIcon />} onClick={() => { handleApprove(selectedVerification); setViewDialogOpen(false); }} disabled={actionLoading || selectedVerification?.status === "approved"}>
            Approve
          </Button>
          <Button variant="contained" color="error" startIcon={<RejectIcon />} onClick={() => { setViewDialogOpen(false); openRejectDialog(selectedVerification); }}>
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Verification</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Please provide a reason for rejecting this verification. The user will be notified via email and push notification.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Rejection Reason"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="e.g., Documents are not clear, Identity mismatch..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleReject} disabled={actionLoading || !rejectReason.trim()}>
            {actionLoading ? <CircularProgress size={20} /> : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Plan Assignment Dialog */}
      <Dialog open={planDialogOpen} onClose={() => setPlanDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Plan to User</DialogTitle>
        <DialogContent>
          {planUser && (
            <Box sx={{ mb: 3, mt: 1 }}>
              <Card variant="outlined">
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Avatar src={getFullUrl(planUser.user?.profileImageUrl)} sx={{ width: 50, height: 50 }}>
                      {planUser.user?.firstName?.charAt(0) || "U"}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {planUser.user?.name || `${planUser.user?.firstName || ''} ${planUser.user?.lastName || ''}`.trim() || "Unknown"}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {planUser.user?.phone || planUser.user?.mobile || "N/A"}
                      </Typography>
                      {planUser.planName && (
                        <Chip label={`Current: ${planUser.planName}`} size="small" color="info" sx={{ mt: 0.5 }} />
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}
          
          <FormControl fullWidth>
            <InputLabel>Select Plan</InputLabel>
            <Select
              value={selectedPlanId}
              onChange={(e) => setSelectedPlanId(e.target.value)}
              label="Select Plan"
            >
              {plans.map((plan) => (
                <MenuItem key={plan._id} value={plan._id}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "center" }}>
                    <Box>
                      <Typography variant="body1" fontWeight="bold">{plan.name}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        Duration: {plan.duration} | Connections: {plan.connectionLimit || "Unlimited"}
                      </Typography>
                    </Box>
                    <Typography variant="body1" fontWeight="bold" color="primary">
                      ₹{plan.offerPrice || plan.price}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Alert severity="info" sx={{ mt: 2 }}>
            The user will be notified via push notification about plan activation.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlanDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="primary" startIcon={<PlanIcon />} onClick={handleAssignPlan} disabled={planLoading || !selectedPlanId}>
            {planLoading ? <CircularProgress size={20} /> : "Assign Plan"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MilanSathiVerifications;
